
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { GuardFormData } from "./types";
import { createGuardWithTeam } from "../../services/guardCreationService";
import { generateCodeFromAFM, generateGuardPassword } from "../../services/utils/guardUtils";

export const useGuardForm = (onSuccess: () => void, onClose: () => void, prepopulatedData?: Partial<GuardFormData>) => {
  const { toast } = useToast();
  const [isAdding, setIsAdding] = useState(false);
  const [operationTimeout, setOperationTimeout] = useState<NodeJS.Timeout | null>(null);
  const [formData, setFormData] = useState<GuardFormData>({
    firstName: prepopulatedData?.firstName || "",
    lastName: prepopulatedData?.lastName || "",
    code: prepopulatedData?.code || "",
    email: prepopulatedData?.email || "",
    afm: prepopulatedData?.afm || "",
    hiringDate: prepopulatedData?.hiringDate || "",
    dateOfBirth: prepopulatedData?.dateOfBirth || "",
    licenseExpiryDate: prepopulatedData?.licenseExpiryDate || "",
    mobilePhone: prepopulatedData?.mobilePhone || "",
    emergencyContact: prepopulatedData?.emergencyContact || "",
    selectedTeamIds: prepopulatedData?.selectedTeamIds || [],
    isManualHire: prepopulatedData?.isManualHire || false,
    gender: prepopulatedData?.gender || "",
  });

  const handleInputChange = (field: keyof GuardFormData) => (
    e: React.ChangeEvent<HTMLInputElement> | { target: { value: any } }
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleTeamChange = (teamIds: string[]) => {
    setFormData((prev) => ({ ...prev, selectedTeamIds: teamIds }));
  };

  const validateForm = (): boolean => {
    // Check required fields
    if (!formData.firstName.trim()) {
      toast({
        title: "Validation Error",
        description: "First name is required",
        variant: "destructive",
      });
      return false;
    }

    if (!formData.lastName.trim()) {
      toast({
        title: "Validation Error",
        description: "Last name is required",
        variant: "destructive",
      });
      return false;
    }

    if (!formData.email.trim()) {
      toast({
        title: "Validation Error",
        description: "Email is required",
        variant: "destructive",
      });
      return false;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Validation Error",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return false;
    }

    if (!formData.afm.trim()) {
      toast({
        title: "Validation Error",
        description: "AFM (tax number) is required",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleCancel = () => {
    if (operationTimeout) {
      clearTimeout(operationTimeout);
      setOperationTimeout(null);
    }
    setIsAdding(false);
    toast({
      title: "Operation Cancelled",
      description: "Guard creation has been cancelled.",
    });
    onClose();
  };

  const handleSubmit = async () => {
    try {
      setIsAdding(true);
      
      // Validate the form first
      if (!validateForm()) {
        setIsAdding(false);
        return;
      }

      // Generate code from AFM if not provided
      const guardCode = formData.code || generateCodeFromAFM(formData.afm);
      
      // Generate the auto password for display purposes
      const autoPassword = generateGuardPassword(formData.afm);
      
      console.log(`Creating guard with auto-generated password pattern: G + last 4 AFM digits + #Secure123`);

      // Set up absolute maximum timeout safeguard (45 seconds)
      const maxTimeout = setTimeout(() => {
        if (isAdding) {
          console.error('Maximum operation time exceeded (45s)');
          setIsAdding(false);
          toast({
            title: "Operation Timed Out",
            description: "The operation took too long. Please try again.",
            variant: "destructive",
          });
        }
      }, 45000);
      
      setOperationTimeout(maxTimeout);

      // Create a timeout promise to prevent indefinite hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timed out after 30 seconds. Please try again.')), 30000);
      });

      const operationPromise = formData.isManualHire
        ? handleManualHire(guardCode)
        : handleAutomaticHire(guardCode);

      // Race between the operation and timeout
      await Promise.race([operationPromise, timeoutPromise]);
      
      // Clear the max timeout if operation completes successfully
      if (maxTimeout) {
        clearTimeout(maxTimeout);
        setOperationTimeout(null);
      }

    } catch (error: any) {
      console.error('Error adding guard:', error);
      
      // Clear timeouts on error
      if (operationTimeout) {
        clearTimeout(operationTimeout);
        setOperationTimeout(null);
      }
      
      toast({
        title: "Error",
        description: error.message || "Failed to add new guard. Please try again.",
        variant: "destructive",
      });
    } finally {
      // Always reset loading state
      setIsAdding(false);
    }
  };

  const handleManualHire = async (guardCode: string) => {
    // Get current user ID
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Create candidate first
    const { data: candidateData, error: candidateError } = await supabase
      .from('candidates')
      .insert({
        full_name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.mobilePhone,
        date_of_birth: formData.dateOfBirth,
        identity_number: formData.afm,
        status: 'hired',
        created_by: user.id
      })
      .select()
      .single();

    if (candidateError) throw candidateError;

    // Create guard data object
    const guardData = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      code: guardCode,
      afm: formData.afm,
      hiring_date: formData.hiringDate,
      date_of_birth: formData.dateOfBirth,
      license_expiry_date: formData.licenseExpiryDate,
      mobile_phone: formData.mobilePhone,
      emergency_contact: formData.emergencyContact,
      gender: formData.gender,
    };

    // Select the first team if any teams are selected
    const teamName = formData.selectedTeamIds.length > 0 
      ? await getTeamNameById(formData.selectedTeamIds[0]) 
      : undefined;

    // Create the guard profile - force new creation from manual form
    const result = await createGuardWithTeam(guardData, teamName, true);

    if (result && candidateData) {
      // Create workflow stage entry for hired status
      await supabase.from('hr_workflow_stages').insert({
        candidate_id: candidateData.id,
        stage_name: 'hired',
        status: 'completed',
        started_at: new Date().toISOString(),
        completed_at: new Date().toISOString(),
        notes: 'Προσλήφθηκε μέσω χειροκίνητης διαδικασίας'
      });

      // Handle additional teams
      if (formData.selectedTeamIds.length > 1 && result.id) {
        for (let i = 1; i < formData.selectedTeamIds.length; i++) {
          const additionalTeamName = await getTeamNameById(formData.selectedTeamIds[i]);
          if (additionalTeamName) {
            await addGuardToTeam(result.id, additionalTeamName);
          }
        }
      }

      toast({
        title: "Guard Hired Manually",
        description: "Guard has been successfully created and marked as hired directly",
      });

      onSuccess();
      onClose();
    }
  };

  const handleAutomaticHire = async (guardCode: string) => {
    // Create the guard data object with the correct field names for the edge function
    const guardData = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      code: guardCode,
      afm: formData.afm,
      hiring_date: formData.hiringDate,
      date_of_birth: formData.dateOfBirth,
      license_expiry_date: formData.licenseExpiryDate,
      mobile_phone: formData.mobilePhone,
      emergency_contact: formData.emergencyContact,
      gender: formData.gender,
    };

    console.log('Submitting guard data:', guardData);

    // Select the first team if any teams are selected
    const teamName = formData.selectedTeamIds.length > 0 
      ? await getTeamNameById(formData.selectedTeamIds[0]) 
      : undefined;

    // Create the guard profile and handle auth user creation - force new creation from manual form
    const result = await createGuardWithTeam(guardData, teamName, true);

    if (result) {
      // If there are additional teams to assign beyond the first one
      if (formData.selectedTeamIds.length > 1 && result.id) {
        for (let i = 1; i < formData.selectedTeamIds.length; i++) {
          const additionalTeamName = await getTeamNameById(formData.selectedTeamIds[i]);
          if (additionalTeamName) {
            await addGuardToTeam(result.id, additionalTeamName);
          }
        }
      }

      toast({
        title: "Guard Added",
        description: `New guard has been successfully created with auto-generated password based on AFM pattern (G + last 4 digits + #Secure123)`,
      });

      onSuccess();
      onClose();
    }
  };

  // Helper function to get team name by ID
  const getTeamNameById = async (teamId: string): Promise<string | undefined> => {
    const { data, error } = await supabase
      .from('teams')
      .select('name')
      .eq('id', teamId)
      .single();
      
    if (error || !data) {
      console.error('Error fetching team name:', error);
      return undefined;
    }
    
    return data.name;
  };

  // Helper function to add guard to an additional team
  const addGuardToTeam = async (guardId: string, teamName: string) => {
    try {
      const { data: existingTeams, error: findTeamError } = await supabase
        .from('teams')
        .select('id')
        .ilike('name', teamName);  // Using ilike for case-insensitive matching

      if (findTeamError) throw findTeamError;

      let teamId;
      if (!existingTeams || existingTeams.length === 0) {
        throw new Error(`Team "${teamName}" not found`);
      } else {
        // Use the first matching team we found
        teamId = existingTeams[0].id;
      }
      
      const { error: teamMemberError } = await supabase
        .from('team_members')
        .insert({
          team_id: teamId,
          user_id: guardId
        });

      if (teamMemberError) throw teamMemberError;
    } catch (error: any) {
      console.error('Error adding guard to team:', error);
      // Don't throw here to allow the process to continue if one team assignment fails
    }
  };

  return {
    formData,
    isAdding,
    handleInputChange,
    handleTeamChange,
    handleSubmit,
    handleCancel,
  };
};
